// src/components/PasswordField.jsx
//
// Shared password UI used by both ForgotPassword.jsx (Reset Password
// step) and SetPassword.jsx (Create Password / accept-invite page),
// matching the "CREATE PASSWORD" mockup: a password input with a
// show/hide eye toggle, plus a live checklist of the 4 rules that
// turns each line from a red X to a green check as the user types.
//
// Self-contained (inline styles only) on purpose -- ForgotPassword.jsx
// pulls in Login.css and SetPassword.jsx doesn't import any custom
// stylesheet at all, so this works correctly on both pages without
// either of them needing new CSS.
import React, { useState } from "react";

function EyeIcon({ crossedOut }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
      {crossedOut && <line x1="2" y1="2" x2="22" y2="22" />}
    </svg>
  );
}

// ---- validation rules, exported so the submit handlers can reuse them ----
export function checkPasswordRules(password) {
  const value = password || "";
  return {
    minLength: value.length >= 8,
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    symbol: /[@$!%*?&]/.test(value),
  };
}

export function isPasswordValid(password) {
  const r = checkPasswordRules(password);
  return r.minLength && r.uppercase && r.lowercase && r.symbol;
}

// ---- password input with a show/hide eye toggle ----
// `inputClassName` lets each page keep its own input styling
// (ForgotPassword uses "form-control-inline", SetPassword uses the
// plain Bootstrap "form-control") -- only the border moves onto the
// wrapper so there's a single outline around input + eye button,
// matching the mockup.
export function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  inputClassName,
  autoComplete,
}) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <input
        id={id}
        name={id}
        type={show ? "text" : "password"}
        className={inputClassName}
        style={{ border: "none", outline: "none", flex: 1, boxShadow: "none" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        style={{
          border: "none",
          borderLeft: "1px solid #d1d5db",
          background: "transparent",
          padding: "0 12px",
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: "#6b7280",
        }}
      >
        <EyeIcon crossedOut={!show} />
      </button>
    </div>
  );
}

// ---- the live "Minimum 8 characters / uppercase / lowercase / symbol" checklist ----
export default function PasswordChecklist({ password }) {
  const rules = checkPasswordRules(password);
  const items = [
    { key: "minLength", label: "Minimum 8 characters" },
    { key: "uppercase", label: "At least one uppercase letter" },
    { key: "lowercase", label: "At least one lowercase letter" },
    { key: "symbol", label: "At least one symbol (@$!%*?&)" },
  ];

  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 16px" }}>
      {items.map((item) => {
        const passed = rules[item.key];
        return (
          <li
            key={item.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              marginBottom: 4,
              color: "#374151",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 14,
                display: "inline-flex",
                justifyContent: "center",
                color: passed ? "#16a34a" : "#dc2626",
                fontWeight: 700,
              }}
            >
              {passed ? "✓" : "✗"}
            </span>
            {item.label}
          </li>
        );
      })}
    </ul>
  );
}